import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { sortBy } from 'lodash';
import { humanize, titleize } from 'underscore.string';
import { validDecimal } from 'utils/accounting-util';
import { useTranslation } from 'react-i18next';
import USER_TYPES from 'constants/UserTypes';
import AuditComments from 'components/modules/comments/AuditComments';
import Alert from 'components/ui/Alert';
import Badge from 'components/v2/Badge';
import StatHeading from 'components/v2/StatHeading';
import ViewAccessorialRequestDocumentRequirements from 'components/modules/shipments/accessorials/ViewAccessorialDocumentRequirements';
import { Skeleton } from '@cargomatic/ui';
import AccessorialActionButtons from './accessorials/AccessorialActionButtons';
import BulkActionsMenu from './accessorials/BulkActionsMenu';
import EquipmentSelector from './accessorials/EquipmentSelector';
import ShipperRateSection from './accessorials/ShipperRateSection';
import CarrierRateSection from './accessorials/CarrierRateSection';
import AccessorialConfirmModal from './accessorials/AccessorialConfirmModal';
import { colorLabelMap, getTotal, } from './accessorials/accessorialRequestPaneUtils';
import useAccessorialRequestData from './hooks/useAccessorialRequestData';
import useAccessorialMutations from './hooks/useAccessorialMutations';
const AUTO_APPLY_NEEDS_REVIEW_INFO_TEXT_MAPPING = {
    true: 'AUTO_APPLY_NEEDS_REVIEW_INFO_TRUE',
    false: 'AUTO_APPLY_NEEDS_REVIEW_INFO_FALSE',
};
const AccessorialRequestPane = ({ shipment: shipmentId, accessorial: accessorialId, onSave, dataTestId, }) => {
    var _a;
    const { t } = useTranslation();
    // Data
    const { fullShipment, accessorial, referenceAccessorial, allEquipmentsById, accessorialFetched, formFields, selectedEquipment, setFormFields, isAccessorialsAdmin, isEditable, refresh, } = useAccessorialRequestData({ shipmentId, accessorialId });
    // Mutations
    const { isEditSaving, isApproveSaving, isDeclineSaving, handleSaveButton, handleRequestAction, handleConfirmBulk, handleEquipmentSelect, } = useAccessorialMutations({
        accessorial,
        accessorialId,
        formFields,
        fullShipment,
        onSave,
        refresh,
    });
    // UI state
    const [editCarrierRate, setEditCarrierRate] = useState(false);
    const [editShipperRate, setEditShipperRate] = useState(false);
    const [modalState, setModalState] = useState(null);
    // Handlers
    const handleEditToggle = useCallback((type) => {
        if (type === USER_TYPES.CARRIER) {
            setEditCarrierRate((prev) => !prev);
        }
        else {
            setEditShipperRate((prev) => !prev);
        }
    }, []);
    const handleChange = useCallback((field, value) => {
        if (!(field.indexOf('unit_qty') > -1 && !validDecimal(value))) {
            setFormFields((prev) => (Object.assign(Object.assign({}, prev), { [field]: value })));
        }
    }, [setFormFields]);
    const handleApproveClick = useCallback((type) => {
        setModalState({ mode: 'single', isDecline: false, confirmType: type });
    }, []);
    const handleDeclineClick = useCallback((type) => {
        setModalState({ mode: 'single', isDecline: true, confirmType: type });
    }, []);
    const handleBulkActionMenu = useCallback((event) => {
        setModalState({
            mode: 'bulk',
            isDecline: event === 'decline-all',
            bulkEvent: event,
        });
    }, []);
    const handleModalConfirm = useCallback(({ declineNote, declineReasonCode }) => {
        if (modalState.mode === 'single') {
            const action = modalState.isDecline ? 'decline' : 'approve';
            handleRequestAction(modalState.confirmType, action, declineNote, declineReasonCode);
        }
        else {
            handleConfirmBulk(modalState.bulkEvent, declineNote, declineReasonCode);
        }
        setModalState(null);
    }, [modalState, handleRequestAction, handleConfirmBulk]);
    const handleModalHide = useCallback(() => setModalState(null), []);
    const renderActions = useCallback((type) => (React.createElement(AccessorialActionButtons, { accessorial: accessorial, accessorialId: accessorialId, shipment: fullShipment, type: type, isAccessorialsAdmin: isAccessorialsAdmin, accessorialFetched: accessorialFetched, isEditSaving: isEditSaving, isApproveSaving: isApproveSaving, isDeclineSaving: isDeclineSaving, isEditable: isEditable, onEdit: handleEditToggle, onApprove: handleApproveClick, onDecline: handleDeclineClick })), [
        accessorial,
        accessorialId,
        fullShipment,
        isAccessorialsAdmin,
        accessorialFetched,
        isEditSaving,
        isApproveSaving,
        isDeclineSaving,
        isEditable,
        handleEditToggle,
        handleApproveClick,
        handleDeclineClick,
    ]);
    // Derived values (must be above early return to respect Rules of Hooks)
    const sortedHistory = useMemo(() => {
        var _a;
        return ((_a = formFields.history) === null || _a === void 0 ? void 0 : _a.length)
            ? sortBy(formFields.history, 'timestamp').reverse()
            : [];
    }, [formFields.history]);
    // Loading state
    if (!accessorial || !fullShipment) {
        return (React.createElement("div", { className: "p-6 flex items-center justify-center" },
            React.createElement(Skeleton, { className: "h-4 w-full" }),
            React.createElement(Skeleton, { className: "h-4 w-full" }),
            React.createElement(Skeleton, { className: "h-4 w-full" }),
            React.createElement(Skeleton, { className: "h-4 w-full" })));
    }
    let payableLabel;
    if (accessorial.payee_type === USER_TYPES.PARTNER) {
        payableLabel = t('PARTNER_REQUESTED_RATE');
    }
    else if (formFields.vendor) {
        payableLabel = t('VENDOR_REQUESTED_RATE');
    }
    else {
        payableLabel = t('CARRIER_REQUESTED_RATE');
    }
    const { auto_applied = false, needs_review = false } = accessorial;
    return (React.createElement("div", { "data-testid": dataTestId, className: "p-6" },
        React.createElement("div", { "data-testid": "div-f1c640a9", className: "flex items-center justify-between" },
            React.createElement("h1", { "data-testid": "h1-685d7482", className: "inline-flex my-0 text-2xl text-grey-darker" },
                formFields.message,
                " (", fullShipment === null || fullShipment === void 0 ? void 0 :
                fullShipment.shipment_id,
                ")"),
            React.createElement(BulkActionsMenu, { accessorial: accessorial, shipment: fullShipment, isAccessorialsAdmin: isAccessorialsAdmin, isEditSaving: isEditSaving, isApproveSaving: isApproveSaving, isDeclineSaving: isDeclineSaving, onBulkAction: handleBulkActionMenu })),
        React.createElement("hr", { "data-testid": "hr-6d5505b5", className: "mt-2 mb-4" }),
        auto_applied && (React.createElement(Alert, { dataTestId: "Alert-Auto-Apply-Needs-Review-Side-Pane", type: "info" }, t(AUTO_APPLY_NEEDS_REVIEW_INFO_TEXT_MAPPING[needs_review]))),
        React.createElement("div", { "data-testid": "div-6012ab9e", className: "flex justify-between my-5" },
            React.createElement(StatHeading, { dataTestId: "StatHeading-fb7f618b", label: accessorial.payer_type === USER_TYPES.CONSIGNEE
                    ? t('CONSIGNEE_SUGGESTED_RATE')
                    : t('SHIPPER_SUGGESTED_RATE'), color: "green", value: getTotal(formFields.shipper_rate, formFields.shipper_unit_qty, accessorial === null || accessorial === void 0 ? void 0 : accessorial.currency) }),
            React.createElement(Badge, { dataTestId: "Badge-0375605f", text: humanize(titleize(formFields.shipper_status)), color: colorLabelMap[formFields.shipper_status] })),
        React.createElement(ShipperRateSection, { editShipperRate: editShipperRate, formFields: formFields, referenceAccessorial: referenceAccessorial, accessorial: accessorial, onChange: handleChange, onSave: () => {
                handleSaveButton(USER_TYPES.SHIPPER);
                handleEditToggle(USER_TYPES.SHIPPER);
            }, onCancel: () => handleEditToggle(USER_TYPES.SHIPPER), renderActions: () => renderActions(USER_TYPES.SHIPPER) }),
        React.createElement("hr", { "data-testid": "hr-b1e5e541" }),
        React.createElement("div", { "data-testid": "div-cd8d1d4f", className: "flex justify-between mb-5" },
            React.createElement(StatHeading, { dataTestId: "StatHeading-825dfaa3", label: payableLabel, color: "green", value: getTotal(formFields.carrier_rate, formFields.carrier_unit_qty, accessorial === null || accessorial === void 0 ? void 0 : accessorial.currency) }),
            React.createElement(Badge, { dataTestId: "Badge-3d549416", text: humanize(titleize(formFields.carrier_status)), color: colorLabelMap[formFields.carrier_status] })),
        React.createElement(CarrierRateSection, { t: t, editCarrierRate: editCarrierRate, formFields: formFields, referenceAccessorial: referenceAccessorial, accessorial: accessorial, fullShipment: fullShipment, onChange: handleChange, onSave: () => {
                handleSaveButton(USER_TYPES.CARRIER);
                handleEditToggle(USER_TYPES.CARRIER);
            }, onCancel: () => handleEditToggle(USER_TYPES.CARRIER), renderActions: () => renderActions(USER_TYPES.CARRIER) }),
        React.createElement("hr", { "data-testid": "hr-0a8894f9" }),
        React.createElement("div", { "data-testid": "div-b6546493" },
            React.createElement("label", { "data-testid": "label-b33c90f8", className: "form-label" },
                t('EQUIPMENT_CONTAINERS'),
                ":")),
        React.createElement("div", { "data-testid": "div-c1b703fb", className: "mt-2" },
            React.createElement(EquipmentSelector, { shipmentEquipment: (fullShipment === null || fullShipment === void 0 ? void 0 : fullShipment.equipments) || [], selectedEquipment: selectedEquipment, allEquipmentsById: allEquipmentsById, onSelect: handleEquipmentSelect })),
        React.createElement("hr", { "data-testid": "hr-682757bf" }),
        React.createElement("div", { "data-testid": "div-80d8e84e", className: "flex flex-col justify-around mt-2" },
            React.createElement(ViewAccessorialRequestDocumentRequirements, { shipment: fullShipment, accessorial: Object.assign(Object.assign({}, accessorial), { slug: accessorial === null || accessorial === void 0 ? void 0 : accessorial.accessorial_type }), foreignKeys: [accessorial === null || accessorial === void 0 ? void 0 : accessorial._id, fullShipment === null || fullShipment === void 0 ? void 0 : fullShipment._id], shipper: ((_a = fullShipment === null || fullShipment === void 0 ? void 0 : fullShipment.shipper) === null || _a === void 0 ? void 0 : _a._id) || (fullShipment === null || fullShipment === void 0 ? void 0 : fullShipment.shipper), isExistingAccessorialRequest: true, onUploadComplete: () => {
                    refresh();
                    onSave === null || onSave === void 0 ? void 0 : onSave();
                } })),
        React.createElement("hr", { "data-testid": "hr-4c2f332e" }),
        React.createElement("div", { "data-testid": "div-4ff31917", className: "flex justify-between" },
            React.createElement(AuditComments, { comments: sortedHistory })),
        modalState && (React.createElement(AccessorialConfirmModal, { show: true, isBulk: modalState.mode === 'bulk', isDecline: modalState.isDecline, confirmType: modalState.confirmType, accessorial: accessorial, onConfirm: handleModalConfirm, onHide: handleModalHide }))));
};
AccessorialRequestPane.propTypes = {
    shipment: PropTypes.string.isRequired,
    accessorial: PropTypes.string.isRequired,
    onSave: PropTypes.func,
    dataTestId: PropTypes.string,
};
export default AccessorialRequestPane;
